import { useTranslation } from 'react-i18next';
import { PlaneTakeoff } from 'lucide-react';
import { cn } from '@/lib/cn';
import type {
  RebookingGroupResult,
  RosterRebookingStatus,
} from '@/lib/disruptions/types';

// disruption-roster-mvp-parity (epic NEX-369 / U1) — the SUGGESTED FLIGHT roster
// cell, mirroring the MVP suggested-flight column (mvp AffectedPassengersList ·
// SuggestedFlightCell). Given the PNR group's rebooking outcome (joined from
// `useRebookingDetail` by `groupId === subCaseUrn`), it shows the winning flight
// with a CONFIRMED badge when the operator has SELECTED it, RECOMMENDED #1 when
// the engine has options but nothing is accepted yet, or a muted empty state
// ("No options" / "Pending"). All labels are friendly; `pnrMasked` PII is
// never read here (this cell only touches flight labels + counts). White-label:
// tone tokens only, no hard-coded brand hex.

interface SuggestedFlightCellProps {
  /** The PNR group's rebooking outcome (rebooking-detail A1), or undefined until
   *  the rebooking-detail read lands / when the group has no result yet. */
  result?: RebookingGroupResult | undefined;
  /** The group's rebooking status — drives CONFIRMED vs RECOMMENDED vs empty. */
  status: RosterRebookingStatus;
}

/** The strategy kinds we carry a friendly label for (i18n:
 *  `roster:suggested.kind.<KIND>`). An unknown kind falls back to the raw code so
 *  the cell still renders something meaningful — this file holds keys, never language. */
const KNOWN_KINDS = new Set([
  'OWN_FLIGHT',
  'INTERLINE',
  'OWN_WITH_LAYOVER',
  'HOTEL_THEN_FLIGHT',
  'SPLIT',
  'AIRLINE_EXTERNAL',
]);

/** The rank-1 (winning) option row — the engine sorts options by rank asc, so
 *  rank 1 is the recommended flight. Falls back to the first row defensively. */
function winningOption(result: RebookingGroupResult | undefined) {
  const options = result?.options ?? [];
  return options.find((o) => o.rank === 1) ?? options[0];
}

export function SuggestedFlightCell({ result, status }: SuggestedFlightCellProps) {
  const { t } = useTranslation('roster');
  const winning = winningOption(result);
  const flightLabel = winning?.flightLabel;
  const kind = result?.best?.kind ?? winning?.kind;

  // QA fix 5 — the winning option already departed (read-seam soft mark): the
  // no-options-style empty variant with an explicit "expired" label, NEVER a
  // fresh "Recommended #1" for a flight that no longer exists.
  if (result?.optionsExpired === true || winning?.expired === true) {
    return (
      <span
        className="text-xs italic text-muted-foreground"
        data-testid="suggested-flight-expired"
      >
        {t('suggested.expired')}
      </span>
    );
  }

  // No winning flight yet → a muted, coded empty state (never blank): NO_OPTIONS /
  // NOT_APPLICABLE reads "No options"; anything still pending reads "Pending".
  if (!flightLabel) {
    const terminal = status === 'NO_OPTIONS' || status === 'NOT_APPLICABLE';
    return (
      <span
        className="text-xs italic text-muted-foreground"
        data-testid="suggested-flight-empty"
      >
        {terminal ? t('suggested.noOptions') : t('suggested.pending')}
      </span>
    );
  }

  const isConfirmed = status === 'SELECTED';
  const kindLabel = kind ? (KNOWN_KINDS.has(kind) ? t(`suggested.kind.${kind}`) : kind) : null;

  return (
    <div className="flex flex-col gap-0.5" data-testid="suggested-flight-cell">
      <span className="inline-flex items-center gap-1 text-xs font-medium text-foreground">
        <PlaneTakeoff className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
        {flightLabel}
      </span>
      {kindLabel ? (
        <span className="text-[10px] text-muted-foreground">{kindLabel}</span>
      ) : null}
      <span
        className={cn(
          'w-fit text-[10px] font-semibold uppercase tracking-wide',
          isConfirmed
            ? 'text-emerald-700 dark:text-emerald-400'
            : 'text-sky-700 dark:text-sky-400',
        )}
        data-testid={isConfirmed ? 'suggested-flight-confirmed' : 'suggested-flight-recommended'}
      >
        {isConfirmed ? t('suggested.confirmed') : t('suggested.recommended')}
      </span>
    </div>
  );
}
